/**
 * User header for authenticated app (dashboard, transactions, etc.).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthContext, useI18n } from '../../providers';
import Dropdown from '../common/Dropdown';
import Logo from '../common/Logo';
import NotificationBell from '../notification/NotificationBell';
import { ROUTES } from '../../utils/constants';
import '../../styles/public-layout.css';

const UserHeader = ({ onMenuToggle, sidebarOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthContext();
  const { language, setLanguage } = useI18n();

  const handleLogout = async () => {
    try {
      await logout();
      navigate(ROUTES.HOME);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const userMenuItems = [
    { label: language === 'fr' ? 'Mon profil' : 'My Profile', onClick: () => navigate(ROUTES.PROFILE) },
    { label: language === 'fr' ? 'Paramètres' : 'Settings', onClick: () => navigate(ROUTES.SETTINGS) },
    { label: language === 'fr' ? 'Notifications' : 'Notifications', onClick: () => navigate(ROUTES.NOTIFICATIONS) },
    { label: language === 'fr' ? 'Changer la langue' : 'Change Language', onClick: () => setLanguage(language === 'fr' ? 'en' : 'fr') },
    { label: language === 'fr' ? 'Déconnexion' : 'Logout', onClick: handleLogout, danger: true },
  ];

  const isOnDashboard = location.pathname === ROUTES.DASHBOARD;
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header className={`header-sleek ${scrolled ? 'is-scrolled' : ''}`}>
      <div className="header-sleek-inner">
        <nav className="header-sleek-nav" aria-label="Main">
          <button
            type="button"
            onClick={() => navigate(ROUTES.DASHBOARD)}
            className="header-sleek-logo"
            aria-label={language === 'fr' ? 'Tableau de bord' : 'Dashboard'}
          >
            <Logo size="header" alt="WekaTrust" />
          </button>
        </nav>

        <div className="header-sleek-actions">
            <button
              type="button"
              onClick={onMenuToggle}
              className="header-sleek-burger lg:hidden"
              aria-label={language === 'fr' ? 'Menu' : 'Menu'}
              aria-expanded={sidebarOpen}
            >
              {sidebarOpen ? (
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            {!isOnDashboard && (
              <button
                type="button"
                onClick={() => navigate(ROUTES.CREATE_TRANSACTION)}
                className="header-sleek-cta hidden lg:inline-flex"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                <span className="hidden xl:inline">
                  {language === 'fr' ? 'Nouvelle transaction' : 'New Transaction'}
                </span>
              </button>
            )}

            <div className="hidden lg:block">
              <NotificationBell />
            </div>

            <button
              type="button"
              onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
              className="header-sleek-lang"
              aria-label="Toggle language"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>

            <Dropdown
              variant="sleek"
              trigger={(isOpen) => (
                <button
                  type="button"
                  className="header-sleek-user"
                  aria-expanded={isOpen}
                  aria-haspopup="true"
                  aria-label={language === 'fr' ? 'Menu compte' : 'Account menu'}
                >
                  <span className="header-sleek-user-avatar">
                    {user?.name?.charAt(0)?.toUpperCase() || user?.fullName?.charAt(0)?.toUpperCase() || user?.email?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                  <span className="header-sleek-user-name">
                    {user?.name || user?.fullName || (language === 'fr' ? 'Utilisateur' : 'User')}
                  </span>
                  <svg className={`header-sleek-user-chevron ${isOpen ? 'header-sleek-user-chevron--open' : ''}`} fill="currentColor" viewBox="0 0 20 20" aria-hidden>
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              )}
              items={userMenuItems.map((item) => ({
                label: item.label,
                onClick: item.onClick,
                danger: item.danger,
              }))}
              position="bottom-right"
            />
        </div>
      </div>
    </header>
  );
};

export default UserHeader;
